import React, { useState, useEffect } from 'react';
import {
  Table, Button, Tag, Modal, Form, Input, Select, InputNumber,
  message, Card, Progress, Space, Popconfirm
} from 'antd';
import {
  PlusOutlined, PlayCircleOutlined, PauseCircleOutlined,
  DeleteOutlined, EyeOutlined
} from '@ant-design/icons';
import api from '../utils/api';

const { Option } = Select;

const Tasks = () => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [templates, setTemplates] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [adAccounts, setAdAccounts] = useState([]);

  useEffect(() => {
    fetchTasks();
    fetchTemplates();
    fetchMaterials();
    fetchAdAccounts();
  }, []);

  const fetchTasks = async () => {
    setLoading(true);
    try {
      const response = await api.get('/tasks/');
      setTasks(response.data);
    } catch (error) {
      message.error('获取任务失败');
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await api.get('/campaign-templates/');
      setTemplates(response.data);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    }
  };

  const fetchMaterials = async () => {
    try {
      const response = await api.get('/materials/');
      // 处理分页响应，提取items数组
      setMaterials(response.data.items || response.data || []);
    } catch (error) {
      console.error('Failed to fetch materials:', error);
      setMaterials([]);
    }
  };

  const fetchAdAccounts = async () => {
    try {
      const response = await api.get('/ad-accounts/');
      setAdAccounts(response.data);
    } catch (error) {
      console.error('Failed to fetch ad accounts:', error);
    }
  };

  const handleCreateTask = async (values) => {
    try {
      const config = {
        template_id: values.template_id,
        ad_account_ids: values.ad_account_ids,
        material_ids: values.material_ids,
        quantity: values.quantity || 1,
      };

      await api.post('/tasks/create-ads', {
        name: values.name,
        config: config,
      });

      message.success('任务创建成功');
      setModalVisible(false);
      form.resetFields();
      fetchTasks();
    } catch (error) {
      message.error(error.response?.data?.detail || '创建失败');
    }
  };

  const handleCancelTask = async (id) => {
    try {
      await api.post(`/tasks/${id}/cancel`);
      message.success('任务已取消');
      fetchTasks();
    } catch (error) {
      message.error('取消失败');
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: 80,
    },
    {
      title: '任务名称',
      dataIndex: 'name',
    },
    {
      title: '类型',
      dataIndex: 'task_type',
      render: (type) => {
        const typeMap = {
          'create_ads': '创建广告',
          'pause_check': '暂停检查',
          'duplicate_check': '复制检查',
          'sync_data': '数据同步',
        };
        return typeMap[type] || type;
      },
    },
    {
      title: '状态',
      dataIndex: 'status',
      render: (status) => {
        const statusMap = {
          'pending': { color: 'default', text: '待执行' },
          'running': { color: 'processing', text: '执行中' },
          'completed': { color: 'success', text: '已完成' },
          'failed': { color: 'error', text: '失败' },
          'cancelled': { color: 'warning', text: '已取消' },
        };
        const { color, text } = statusMap[status] || { color: 'default', text: status };
        return <Tag color={color}>{text}</Tag>;
      },
    },
    {
      title: '进度',
      dataIndex: 'progress_percent',
      render: (percent, record) => (
        <div style={{ width: 120 }}>
          <Progress 
            percent={percent} 
            size="small"
            status={record.status === 'failed' ? 'exception' : undefined}
          />
          <div style={{ fontSize: 12, color: '#999' }}>
            {record.processed_items}/{record.total_items}
          </div>
        </div>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      render: (date) => date ? new Date(date).toLocaleString('zh-CN') : '-',
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space>
          {['pending', 'running'].includes(record.status) && (
            <Popconfirm
              title="确定取消任务?"
              onConfirm={() => handleCancelTask(record.id)}
            >
              <Button icon={<PauseCircleOutlined />} size="small" danger>
                取消
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
        <h2>任务管理</h2>
        <Button 
          type="primary" 
          icon={<PlusOutlined />}
          onClick={() => setModalVisible(true)}
        >
          创建任务
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={tasks}
        rowKey="id"
        loading={loading}
      />

      <Modal
        title="创建广告任务"
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={() => form.submit()}
        width={700}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleCreateTask}
        >
          <Form.Item
            name="name"
            label="任务名称"
            rules={[{ required: true, message: '请输入任务名称' }]}
          >
            <Input placeholder="输入任务名称" />
          </Form.Item>

          <Form.Item
            name="template_id"
            label="广告模板"
            rules={[{ required: true, message: '请选择模板' }]}
          >
            <Select placeholder="选择模板">
              {templates.map(tpl => (
                <Option key={tpl.id} value={tpl.id}>{tpl.name}</Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="ad_account_ids"
            label="广告账户"
            rules={[{ required: true, message: '请选择广告账户' }]}
          >
            <Select mode="multiple" placeholder="选择广告账户">
              {adAccounts.map(acc => (
                <Option key={acc.id} value={acc.id}>{acc.name}</Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="material_ids"
            label="素材"
            rules={[{ required: true, message: '请选择素材' }]}
          >
            <Select mode="multiple" placeholder="选择素材">
              {materials.map(mat => (
                <Option key={mat.id} value={mat.id}>{mat.name}</Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="quantity"
            label="每个素材创建数量"
            initialValue={1}
          >
            <InputNumber min={1} max={10} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Tasks;
